import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App, Button } from 'antd'
import type { RentalOrder } from '@super-mega/server/contracts/order'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { CancelRental } from './rental-cancel-modal.tsx'
import { EndRental } from './rental-end-modal.tsx'
import { RenewRental } from './rental-renew-modal.tsx'

export function RentalLifecycleActions({ order }: { order: RentalOrder }) {
  const access = useAccess()
  const trpc = useTRPC(),
    cache = useQueryClient(),
    { message, modal } = App.useApp()
  const activate = useMutation(trpc.order.rental.activate.mutationOptions())
  const [cancelling, setCancelling] = useState(false)
  const [ending, setEnding] = useState(false),
    [renewing, setRenewing] = useState(false)
  return (
    <div className="flex flex-wrap gap-2">
      {access.can('order.rental.activate') && order.status === 'awaitingBilling' && (
        <Button
          type="primary"
          loading={activate.isPending}
          onClick={() =>
            modal.confirm({
              title: '确认租赁生效',
              content: '合同及首期费用已初始化后可以挂账履约，首期欠款继续保留。请核对空间和租期。',
              onOk: async () => {
                try {
                  await activate.mutateAsync({ rentalOrderId: order.id, expectedUpdatedAt: order.updatedAt })
                  await Promise.all([
                    cache.invalidateQueries(trpc.order.rental.detail.queryFilter()),
                    cache.invalidateQueries(trpc.order.rental.list.queryFilter()),
                    cache.invalidateQueries(trpc.room.list.queryFilter()),
                    cache.invalidateQueries(trpc.room.availableOptions.queryFilter())
                  ])
                  void message.success('租赁已生效，欠款仍按账单管理')
                } catch (e) {
                  void message.error(e instanceof Error ? e.message : '生效失败')
                  throw e
                }
              }
            })
          }
        >
          确认生效
        </Button>
      )}
      {access.can('order.rental.cancel') && ['pending', 'awaitingBilling'].includes(order.status) && (
        <Button danger onClick={() => setCancelling(true)}>
          取消未生效租赁
        </Button>
      )}
      {cancelling && <CancelRental id={order.id} onClose={() => setCancelling(false)} />}
      {access.can('order.rental.end') && ['active', 'awaitingBilling'].includes(order.status) && (
        <Button danger onClick={() => setEnding(true)}>
          结束租赁
        </Button>
      )}
      {access.all(['order.rental.renew', 'order.rental.create']) && ['active', 'ended'].includes(order.status) && (
        <Button onClick={() => setRenewing(true)}>续租新订单</Button>
      )}
      {ending && <EndRental order={order} onClose={() => setEnding(false)} />}
      {renewing && <RenewRental order={order} onClose={() => setRenewing(false)} />}
    </div>
  )
}
